import decode from 'jwt-decode';
import { isNil, isEmpty, isString } from 'lodash';
import { removeCookie } from './CookieUtils';

export const decodeToken = (token) => decode(token);

const setUserCompany = (companyId = '') => window.localStorage.setItem('companyId', companyId);
const setUserCompanyPermalink = (permalink = '') => window.localStorage.setItem('companyPermalink', permalink);
const setUserLanguage = (languageCode = '') => window.localStorage.setItem('languageCode', languageCode);
const setToken = (idToken) => window.localStorage.setItem('id_token', idToken);
const setRefreshToken = (rid) => window.localStorage.setItem('refreshToken', rid);
const setVerbAuthId = (verbAuthId) => window.localStorage.setItem('verbAuthId', verbAuthId);

export const setUserValues = (params) => {
    const { idToken, idTokenPayload, rid } = params;
    const profile = idTokenPayload[Config.AUTH0_METADATA_NAMESPACE] || idTokenPayload.app_metadata || {};
    setToken(idToken);
    setUserCompany(profile.companyId);
    setUserCompanyPermalink(profile.companyPermalink);
    setUserLanguage(profile.language);
    if (rid) {
        setRefreshToken(rid);
    }
    setVerbAuthId(profile.verbAuthId || '');
};

export default class AuthService {
    constructor(clientId, domain) {
        // Configure Auth0
    }

    tokenRenewalTimeout;

    static getTokenExpirationDate = (token) => {
        if (isNil(token) || isEmpty(token) || !isString(token)) {
            return null;
        }

        const decoded = decode(token);
        if (!decoded.exp) {
            return null;
        }

        const date = new Date(0); // The 0 here is the key, which sets the date to the epoch
        date.setUTCSeconds(decoded.exp);
        return date;
    };

    static isLoggedIn() {
        const isTokenExpired = (token) => {
            const date = this.getTokenExpirationDate(token);
            if (date === null) {
                return false;
            }

            return !(date.valueOf() > new Date().valueOf());
        };

        // Checks if there is a saved token and it's still valid
        const token = this.getToken();
        return !!token && !isTokenExpired(token);
    }

    /**
     *
     * @param {number} minutes
     * @returns {boolean}
     */
    static isTokenAboutToExpire(minutes) {
        const token = this.getToken();
        const expirationDate = this.getTokenExpirationDate(token);
        if (expirationDate === null) {
            return false;
        }

        const numberOfMilliseconds = minutes * 60 * 1000;

        const currentWithBuffer = new Date().valueOf() + numberOfMilliseconds;
        return expirationDate.valueOf() < currentWithBuffer;
    }

    /**
     * @param {String} userId
     */
    static setUserId(userId = '') {
        // Saves profile data to localStorage
        window.localStorage.setItem('userId', userId);
    }

    static setUserCompany(companyId = '') {
        // Saves the profile companyID to localStorage
        return window.localStorage.setItem('companyId', companyId);
    }

    static setUserCompanyPermalink(permalink = '') {
        // Saves the company permalink to localStorage for routing purposes
        return window.localStorage.setItem('companyPermalink', permalink);
    }

    static setUserLanguage(languageCode = '') {
        // Saves preferred user language to localStorage
        return window.localStorage.setItem('languageCode', languageCode);
    }

    static setUserVerbAuthId(verbAuthId = '') {
        // Saves user verbAuthId to localStorage
        return window.localStorage.setItem('verbAuthId', verbAuthId);
    }

    static setRefreshToken(rid = '') {
        // Saves user refreshToken to localStorage
        return window.localStorage.setItem('refreshToken', rid);
    }

    static getUserCompany() {
        // Retrieves the profile companyID from localStorage
        return window.localStorage.getItem('companyId');
    }

    static getUserCompanyPermalink() {
        // Retrieves the company permalink to localStorage for routing purposes
        return window.localStorage.getItem('companyPermalink');
    }

    static getUserLanguage() {
        // Retrieves the preferred user language
        return window.localStorage.getItem('languageCode');
    }

    static getUserVerbAuthId() {
        // Retrieves the user verbAuthId
        return window.localStorage.getItem('verbAuthId');
    }

    static getRefreshToken() {
        // Retrieves the user refreshToken
        return window.localStorage.getItem('refreshToken');
    }

    setToken(idToken, ignoreRenewal) {
        // Saves user token to local storage
        window.localStorage.setItem('id_token', idToken);
        if (!ignoreRenewal) {
            this.scheduleRenewal();
        }
    }

    static getToken() {
        // Retrieves the user token from local storage
        return window.localStorage.getItem('id_token');
    }

    logout(route) {
        // Clear user token and profile data from localStorage
        window.localStorage.removeItem('id_token');
        window.localStorage.removeItem('profile');
        window.localStorage.removeItem('companyPermalink');
        window.localStorage.removeItem('userId');
        window.localStorage.removeItem('languageCode');
        window.localStorage.removeItem('companyId');
        window.localStorage.removeItem('verbAuthId');
        window.localStorage.removeItem('refreshToken');
        window.localStorage.removeItem('ajs_user_id');
        removeCookie('ajs_user_id');

        clearTimeout(this.tokenRenewalTimeout);

        return history.push('/errorpage');       
    }

    /**
     *
     * @param {String} newLocation
     * @private
     */
    static _setLocation(newLocation) {
        window.location = newLocation;
    }
}
